import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar, FilterConfig } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { toastService } from '@services/toast.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';
import type { LegacyListParams, LegacyListResponse } from '@services/pm-legacy.service.ts';

export interface LegacyColumn<T> {
  label: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  nowrap?: boolean;
}

interface LegacyListTableProps<T extends { id: number; isDeleted: boolean }> {
  storageKey: string;
  searchLabel: string;
  columns: LegacyColumn<T>[];
  fetch: (params: LegacyListParams) => Promise<LegacyListResponse<T>>;
  /** Organization filter (clients, experts); initial value - from the URL (?orgId=). */
  withOrgFilter?: boolean;
  initialOrgId?: string;
}

/** Read-only list of legacy records: search, optional organization filter, deleted toggle, pagination. */
export function LegacyListTable<T extends { id: number; isDeleted: boolean }>({
  storageKey, searchLabel, columns, fetch, withOrgFilter = false, initialOrgId = '',
}: LegacyListTableProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [orgId, setOrgId] = useState(initialOrgId);
  const [deleted, setDeleted] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const debouncedOrgId = useDebounce(orgId, 400);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const orgNumber = Number(debouncedOrgId);
      const res = await fetch({
        page, limit,
        search: debouncedSearch.trim() || undefined,
        orgId: withOrgFilter && Number.isInteger(orgNumber) && orgNumber > 0 ? orgNumber : undefined,
        includeDeleted: deleted === 'include',
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити дані старої студії.');
    } finally {
      setLoading(false);
    }
  }, [fetch, page, limit, debouncedSearch, debouncedOrgId, deleted, withOrgFilter]);

  useEffect(() => { load(); }, [load]);

  const filters: FilterConfig[] = [
    { id: 'search', type: 'text', label: searchLabel, value: search, onChange: (v: string) => { setSearch(v); setPage(1); }, flexGrow: 1 },
    ...(withOrgFilter
      ? [{ id: 'orgId', type: 'text' as const, label: 'ID організації', value: orgId, onChange: (v: string) => { setOrgId(v); setPage(1); } }]
      : []),
    {
      id: 'deleted',
      type: 'select',
      label: 'Видалені',
      value: deleted,
      onChange: (v: string) => { setDeleted(v); setPage(1); },
      options: [{ value: '', label: 'Без видалених' }, { value: 'include', label: 'З видаленими' }],
    },
  ];

  return (
    <PageCard>
      <PageToolbar
        // An organization from the URL must not be replaced by saved filters.
        storageKey={initialOrgId ? undefined : `filters:${storageKey}`}
        filters={filters}
        onReset={() => { setSearch(''); setOrgId(''); setDeleted(''); setPage(1); }}
        showReset
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Записів не знайдено</Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => <TableCell key={c.label} align={c.align}>{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} hover sx={row.isDeleted ? { opacity: 0.5 } : undefined}>
                  {columns.map((c) => (
                    <TableCell key={c.label} align={c.align} sx={c.nowrap ? { whiteSpace: 'nowrap' } : undefined}>
                      {c.render(row) ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey={`pagination:${storageKey}`}
      />
    </PageCard>
  );
}

export const yesNo = (value: boolean) => (value ? 'так' : '—');
